import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Tabs,
  Tab,
  Button,
  Table,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Chart from "chart.js/auto";
import api from "../../services/api";
import { toast } from "react-toastify";

const DashboardIndex = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [newPostsCount, setNewPostsCount] = useState(0);
  const [newCommentsCount, setNewCommentsCount] = useState(0);
  const [newReactionsCount, setNewReactionsCount] = useState(0);
  const [newUserCount, setNewUserCount] = useState(0);
  const [chartData, setChartData] = useState({
    labels: [],
    postData: [],
    commentData: [],
    reactData: [],
    userData: [],
  });
  const [filter, setFilter] = useState("daily"); // Inicialmente 'diário'

  const postsChartRef = useRef(null);
  const commentsChartRef = useRef(null);
  const reactionsChartRef = useRef(null);
  const usersChartRef = useRef(null);

  const postsChartInstance = useRef(null);
  const commentsChartInstance = useRef(null);
  const reactionsChartInstance = useRef(null);
  const usersChartInstance = useRef(null);
  const [ranking, setRanking] = useState([]);

  const [activeTab, setActiveTab] = useState("users");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const groupDataByInterval = (dates, data, interval) => {
    const grouped = dates.reduce((acc, date, index) => {
      let key;
      switch (interval) {
        case "monthly":
          key = getMonthName(date);
          break;
        case "bimonthly":
          key = getBimonthly(date);
          break;
        case "semester":
          key = getSemester(date);
          break;
        case "annual":
          key = `Ano ${date.split("/")[2]}`;
          break;
        default:
          key = date; // Default 'daily'
      }

      if (!acc[key]) acc[key] = 0;
      acc[key] += data[index];
      return acc;
    }, {});

    return grouped;
  };

  const getMonthName = (date) => {
    const months = [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ];
    return months[parseInt(date.split("/")[1]) - 1]; // Mês do ano
  };

  const getBimonthly = (date) => {
    const month = parseInt(date.split("/")[1]);
    return Math.ceil(month / 2); // Agrupar bimestral
  };

  const getSemester = (date) => {
    const month = parseInt(date.split("/")[1]);
    const semester = Math.ceil(month / 6); // Agrupar bimestral

    return semester <= 6 ? 1 : 2; // Semestre 1 ou 2
  };

  const fetchData = async () => {
    try {
      const response = await api.get("api/dashboard/index", {
        params: { start_date: startDate, end_date: endDate, filter: filter },
      });

      if (response.data.error) {
        toast.error(response.data.error);
      }

      setNewPostsCount(response.data.pcount);
      setNewCommentsCount(response.data.ccount);
      setNewReactionsCount(response.data.rcount);
      setNewUserCount(response.data.ucount);

      const groupedPostData = groupDataByInterval(
        response.data.labels,
        response.data.postData,
        filter
      );
      const groupedCommentData = groupDataByInterval(
        response.data.labels,
        response.data.commentData,
        filter
      );
      const groupedReactData = groupDataByInterval(
        response.data.labels,
        response.data.reactData,
        filter
      );
      const groupedUserData = groupDataByInterval(
        response.data.labels,
        response.data.userData,
        filter
      );

      setChartData({
        labels: Object.keys(groupedPostData),
        postData: Object.values(groupedPostData),
        commentData: Object.values(groupedCommentData),
        reactData: Object.values(groupedReactData),
        userData: Object.values(groupedUserData),
      });

      if (response.data.error) {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error("Erro ao buscar dados.");
    }
  };

  const renderChart = (chartRef, chartInstance, label, data, color) => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: chartData.labels,
        datasets: [
          {
            label,
            data,
            backgroundColor: color.backgroundColor,
            borderColor: color.borderColor,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Intervalo",
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Quantidade",
            },
          },
        },
      },
    });
  };

  const handleDownload = () => {
    let activeChartInstance;

    switch (activeTab) {
      case "users":
        activeChartInstance = usersChartInstance.current;
        break;
      case "posts":
        activeChartInstance = postsChartInstance.current;
        break;
      case "comments":
        activeChartInstance = commentsChartInstance.current;
        break;
      case "reactions":
        activeChartInstance = reactionsChartInstance.current;
        break;
      default:
        console.error("Nenhum gráfico ativo para download.");
        toast.error("Instância do gráfico ativo não encontrada.");
        return;
    }

    if (activeChartInstance) {
      const imageUrl = activeChartInstance.toBase64Image();
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `grafico_${activeTab}.png`;
      link.click();
    } else {
      console.error("Instância do gráfico ativo não encontrada.");
      toast.error("Instância do gráfico ativo não encontrada.");
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, filter]);

  const rootStyles = getComputedStyle(document.documentElement);
  const primaryColor = rootStyles.getPropertyValue("--bs-primary").trim();

  useEffect(() => {
    if (activeTab === "posts" && chartData.postData.length) {
      renderChart(
        postsChartRef,
        postsChartInstance,
        "Posts",
        chartData.postData,
        {
          backgroundColor: primaryColor + "66",
          borderColor: primaryColor,
        }
      );
    }
  }, [activeTab, chartData.postData]);

  useEffect(() => {
    if (activeTab === "comments" && chartData.commentData.length) {
      renderChart(
        commentsChartRef,
        commentsChartInstance,
        "Comentários",
        chartData.commentData,
        {
          backgroundColor: primaryColor + "66",
          borderColor: primaryColor,
        }
      );
    }
  }, [activeTab, chartData.commentData]);

  useEffect(() => {
    if (activeTab === "reactions" && chartData.reactData.length) {
      renderChart(
        reactionsChartRef,
        reactionsChartInstance,
        "Reações",
        chartData.reactData,
        {
          backgroundColor: primaryColor + "66",
          borderColor: primaryColor,
        }
      );
    }
  }, [activeTab, chartData.reactData]);

  useEffect(() => {
    if (activeTab === "users" && chartData.userData.length) {
      renderChart(
        usersChartRef,
        usersChartInstance,
        "Usuários",
        chartData.userData,
        {
          backgroundColor: primaryColor + "66",
          borderColor: primaryColor,
        }
      );
    }
  }, [activeTab, chartData.userData]);

  const fetchRanking = async () => {
    try {
      const response = await api.get("/api/dashboard/interactions-ranking", {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setRanking(response.data);
    } catch (error) {
      toast.error("Erro ao buscar ranking de interações");
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchRanking();
    }
  }, [startDate, endDate]);

  return (
    <div id="content-page" className="content-inner">
      <Container>
        <Row>
          <Col sm="12">
            <Card className="position-relative inner-page-bg bg-primary p-5">
              <div
                className="inner-page-title"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "-webkit-fill-available",
                }}
              >
                <h3 className="text-white">Dashboard</h3>
                <Link to="/" className="text-white py-1 px-2">
                  <i className="icon material-symbols-outlined d-flex">
                    arrow_back
                  </i>
                </Link>
              </div>
            </Card>
          </Col>
          <Col sm="12" lg="12">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-end">
                <Form className="d-flex gap-3 align-items-center">
                  <Form.Group>
                    <Form.Label>Data Início</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Data Fim</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Exibição</Form.Label>
                    <Form.Control
                      as="select"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <option value="daily">Diário</option>
                      <option value="monthly">Mensal</option>
                      <option value="bimonthly">Bimestral</option>
                      <option value="semester">Semestral</option>
                      <option value="annual">Anual</option>
                    </Form.Control>
                  </Form.Group>
                </Form>
                <Button className="btn-lg" onClick={handleDownload}>
                  <i className="material-symbols-outlined d-flex">download</i>
                </Button>
              </Card.Header>
              <Card.Body>
                <Tabs
                  activeKey={activeTab}
                  onSelect={handleTabChange}
                  id="dashboard-tabs"
                  className="mb-3"
                  justify
                  style={{ gap: "10px" }}
                >
                  <Tab
                    eventKey="users"
                    title={
                      <>
                        <b className="fs-6 mx-2">Usuários</b>
                        <span className="badge bg-primary">{newUserCount}</span>
                      </>
                    }
                  >
                    <canvas ref={usersChartRef} />
                  </Tab>
                  <Tab
                    eventKey="posts"
                    title={
                      <>
                        <b className="fs-6 mx-2">Posts</b>
                        <span className="badge bg-primary">
                          {newPostsCount}
                        </span>
                      </>
                    }
                  >
                    <canvas ref={postsChartRef} />
                  </Tab>

                  <Tab
                    eventKey="comments"
                    title={
                      <>
                        <b className="fs-6 mx-2">Comentários</b>
                        <span className="badge bg-primary">
                          {newCommentsCount}
                        </span>
                      </>
                    }
                  >
                    <canvas ref={commentsChartRef} />
                  </Tab>
                  <Tab
                    eventKey="reactions"
                    title={
                      <>
                        <b className="fs-6 mx-2">Reações</b>
                        <span className="badge bg-primary">
                          {newReactionsCount}
                        </span>
                      </>
                    }
                  >
                    <canvas ref={reactionsChartRef} />
                  </Tab>
                  <Tab
                    eventKey="ranking"
                    title={
                      <>
                        <b className="fs-6 mx-2">Ranking</b>
                        <span className="badge bg-primary">
                          <i className="material-symbols-outlined d-flex" style={{fontSize:15}}>
                            crown
                          </i>
                        </span>
                      </>
                    }
                  >
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Usuário</th>
                          <th>Posts</th>
                          <th>Comentários</th>
                          <th>Reações</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ranking.map((user, index) => (
                          <tr key={index}>
                            <td>{user.name}</td>
                            <td>{user.posts}</td>
                            <td>{user.comments}</td>
                            <td>{user.reactions}</td>
                            <td>{user.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DashboardIndex;
